import { supabase } from "../supabase";
import {
  clearSessionMaterials,
  commitSessionMaterialsDurably,
  flushSessionMaterialSync,
  getSessionMaterials,
  getUnsyncedSessionMaterialLines,
  reconcileSessionMaterialLines,
  resolveTimeLogId,
  saveSessionMaterials,
} from "./sessionMaterials";
import { ensureTimelogQuoteLineLink } from "./projectLabourLedger";
import { traceTimelogWrite } from "./timelogWriteTrace";
import { devLog } from "./devLog";

export const FINALIZE_TRACE_PATH = "TimeTrackingV2Screen.handleFinalizeSession";

export function buildTimelogUpdatePayload({
  reviewTimelog,
  workCompleted,
  category,
  notes,
}) {
  return {
    start_time: reviewTimelog?.start_time,
    end_time: reviewTimelog?.end_time,
    date_iso: reviewTimelog?.date_iso,
    total_hours: reviewTimelog?.total_hours,
    total_seconds: reviewTimelog?.total_seconds,
    break_seconds: reviewTimelog?.break_seconds,
    net_work_seconds: reviewTimelog?.net_work_seconds,
    work_completed: workCompleted,
    work_category: category,
    notes: notes,
  };
}

/**
 * Critical finalize path: persist timelog fields and drop the pending queue entry
 * when no material lines remain to sync.
 */
export async function runCriticalTimelogFinalize({
  sessionId,
  reviewTimelog,
  workCompleted,
  category,
  notes,
  materialLines = [],
  getPendingTimeLogs,
  savePendingTimeLogs,
  currentUser,
  updateTimelog = defaultUpdateTimelog,
}) {
  traceTimelogWrite({
    operation: "update",
    path: FINALIZE_TRACE_PATH,
    sessionId,
    phase: "attempt",
  });

  const { data, error } = await updateTimelog(
    sessionId,
    buildTimelogUpdatePayload({ reviewTimelog, workCompleted, category, notes }),
  );

  if (error) {
    traceTimelogWrite({
      operation: "update",
      path: FINALIZE_TRACE_PATH,
      sessionId,
      phase: "error",
      error,
    });
    devLog("OFFLINE FINALIZE SESSION");
    return { timelogSynced: false, timeLogId: null };
  }

  traceTimelogWrite({
    operation: "update",
    path: FINALIZE_TRACE_PATH,
    sessionId,
    phase: "success",
  });

  if (!materialLines.length) {
    const remainingPending = getPendingTimeLogs(currentUser).filter(
      (log) => log.session_id !== sessionId,
    );
    savePendingTimeLogs(remainingPending, currentUser);
  }

  return { timelogSynced: true, timeLogId: data?.id ?? null };
}

async function defaultUpdateTimelog(sessionId, payload) {
  return supabase
    .from("time_logs")
    .update(payload)
    .eq("session_id", sessionId)
    .select("id")
    .maybeSingle();
}

/**
 * Optional quote-line linking. Failures never block labour or materials.
 */
export async function runOptionalFinalizeQuoteLink({
  sessionId,
  reviewTimelog,
  workCompleted,
  category,
  resolveTimeLogIdFn = resolveTimeLogId,
  ensureQuoteLineLinkFn = ensureTimelogQuoteLineLink,
}) {
  if (!sessionId || !reviewTimelog?.project_id) {
    return { linked: false };
  }

  try {
    const timeLogId = await resolveTimeLogIdFn(sessionId);
    if (!timeLogId) {
      return { linked: false };
    }
    await ensureQuoteLineLinkFn(timeLogId, reviewTimelog.project_id, {
      work_completed: workCompleted,
      work_category: category,
      trade_name: reviewTimelog?.trade_name,
    });
    return { linked: true };
  } catch (quoteError) {
    console.error("FINALIZE QUOTE LINE LINK ERROR:", quoteError);
    return { linked: false, error: quoteError };
  }
}

/**
 * Required material persistence for finalize. Labour may already be saved.
 * Returns a structured result so the UI can show Mission Complete only after
 * durable confirmation, or an explicit labour-saved / materials-retry state.
 */
export async function finalizeRequiredSessionMaterials({
  sessionId,
  reviewTimelog,
  materialLines = null,
  materialCommitIdempotencyKey,
  enteredBy,
  usageDate,
  timeLogId: timeLogIdOverride = null,
  getPendingTimeLogs,
  savePendingTimeLogs,
  currentUser,
  resolveTimeLogIdFn = resolveTimeLogId,
  commitMaterialsFn = commitSessionMaterialsDurably,
  clearMaterialsFn = clearSessionMaterials,
}) {
  await flushSessionMaterialSync(sessionId);
  const snapshot = reconcileSessionMaterialLines(
    getSessionMaterials(sessionId),
    materialLines || [],
  );
  if (materialLines?.length) {
    saveSessionMaterials(
      sessionId,
      snapshot,
    );
  }

  const timeLogId =
    timeLogIdOverride ?? (await resolveTimeLogIdFn(sessionId));
  const materialResult = await commitMaterialsFn({
    sessionId,
    projectId: reviewTimelog?.project_id,
    enteredBy,
    timeLogId,
    usageDate,
    lines: getSessionMaterials(sessionId),
    idempotencyKey: materialCommitIdempotencyKey,
    actor: currentUser,
  });

  const confirmed = materialResult?.confirmed === true;
  const remaining = getUnsyncedSessionMaterialLines(
    getSessionMaterials(sessionId),
  );

  if (!confirmed || remaining.length) {
    console.error(
      "SESSION MATERIALS COMMIT ERROR:",
      materialResult?.error ||
        new Error("Materials persistence not confirmed; retaining local drafts."),
    );
    devLog("FINALIZE SESSION — materials remain queued for retry");
    return {
      materialsConfirmed: false,
      skipped: false,
      error:
        materialResult?.error ||
        new Error("Materials could not be saved. Please retry."),
      timeLogId,
    };
  }

  clearMaterialsFn(sessionId);

  const remainingPending = getPendingTimeLogs(currentUser).filter(
    (log) => log.session_id !== sessionId,
  );
  savePendingTimeLogs(remainingPending, currentUser);

  return {
    materialsConfirmed: true,
    skipped: Boolean(materialResult?.skipped),
    error: null,
    timeLogId,
    commitBatchId: materialResult?.commitBatchId || null,
  };
}

/**
 * Non-critical enrichment kept for offline pending-queue retry compatibility.
 * Callers that need honest finalize should use finalizeRequiredSessionMaterials.
 */
export async function runSessionFinalizeEnrichment({
  sessionId,
  reviewTimelog,
  workCompleted,
  category,
  materialLines = [],
  materialCommitIdempotencyKey,
  enteredBy,
  usageDate,
  getPendingTimeLogs,
  savePendingTimeLogs,
  currentUser,
  resolveTimeLogIdFn = resolveTimeLogId,
  ensureQuoteLineLinkFn = ensureTimelogQuoteLineLink,
  commitMaterialsFn = commitSessionMaterialsDurably,
  clearMaterialsFn = clearSessionMaterials,
}) {
  if (!sessionId) {
    return { materialsConfirmed: true, skipped: true };
  }

  await runOptionalFinalizeQuoteLink({
    sessionId,
    reviewTimelog,
    workCompleted,
    category,
    resolveTimeLogIdFn,
    ensureQuoteLineLinkFn,
  });

  if (!materialLines.length) {
    return { materialsConfirmed: true, skipped: true };
  }

  return finalizeRequiredSessionMaterials({
    sessionId,
    reviewTimelog,
    materialLines,
    materialCommitIdempotencyKey,
    enteredBy,
    usageDate,
    getPendingTimeLogs,
    savePendingTimeLogs,
    currentUser,
    resolveTimeLogIdFn,
    commitMaterialsFn,
    clearMaterialsFn,
  });
}
